"use client";

import { type ReactNode, createContext, useCallback, useContext, useRef, useState } from "react";

interface FullscreenContextValue {
  isFullscreen: boolean;
  isPseudoFullscreen: boolean;
  toggleFullscreen: () => Promise<void>;
  registerToggleFullscreen: (toggleFn: () => Promise<void>) => void;
  registerFullscreenState: (isFullscreen: boolean, isPseudoFullscreen: boolean) => void;
}

const FullscreenContext = createContext<FullscreenContextValue | undefined>(undefined);

interface FullscreenProviderProps {
  children: ReactNode;
}

/**
 * Provider component that manages fullscreen state and toggle function
 * Game player components register their toggle functions and state here,
 * allowing sibling components (like GameHeader) to trigger fullscreen
 */
export function FullscreenProvider({ children }: FullscreenProviderProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPseudoFullscreen, setIsPseudoFullscreen] = useState(false);
  const toggleFullscreenRef = useRef<(() => Promise<void>) | null>(null);

  const registerToggleFullscreen = useCallback((toggleFn: () => Promise<void>) => {
    toggleFullscreenRef.current = toggleFn;
  }, []);

  const registerFullscreenState = useCallback((fullscreen: boolean, pseudoFullscreen: boolean) => {
    setIsFullscreen(fullscreen);
    setIsPseudoFullscreen(pseudoFullscreen);
  }, []);

  const toggleFullscreen = useCallback(async () => {
    if (toggleFullscreenRef.current) {
      await toggleFullscreenRef.current();
    }
  }, []);

  return (
    <FullscreenContext.Provider
      value={{
        isFullscreen,
        isPseudoFullscreen,
        toggleFullscreen,
        registerToggleFullscreen,
        registerFullscreenState,
      }}
    >
      {children}
    </FullscreenContext.Provider>
  );
}

/**
 * Hook to access fullscreen context
 * @throws Error if used outside FullscreenProvider
 */
export function useFullscreenContext(): FullscreenContextValue {
  const context = useContext(FullscreenContext);
  if (context === undefined) {
    throw new Error("useFullscreenContext must be used within a FullscreenProvider");
  }
  return context;
}
